import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Users, ClipboardList, Calendar, Filter, Search, Edit } from 'lucide-react';
import { useScoutTasks } from '@/hooks/useScoutTasks';
import { useScoutTaskAssignments } from '@/hooks/useScoutTaskAssignments';
import ReusableTaskCreator from '@/components/scout/ReusableTaskCreator';
import AssignTaskModal from '@/components/scout/AssignTaskModal';
import EditAssignedTaskModal from '@/components/scout/EditAssignedTaskModal';
import { toast } from 'sonner';

const TasksPage: React.FC = () => {
  const { tasks, isLoading: tasksLoading } = useScoutTasks();
  const { assignments, updateAssignment, isLoading: assignmentsLoading } = useScoutTaskAssignments();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const handleTaskAssigned = () => {
    toast.success('Task assigned successfully!');
    setIsAssignModalOpen(false);
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setIsEditModalOpen(true);
  };

  const handleSaveAssignment = async (assignmentId: string, updates: any) => {
    const success = await updateAssignment(assignmentId, updates);
    if (success) {
      toast.success('Assignment updated successfully!');
      setIsEditModalOpen(false);
      setEditingAssignment(null);
    } else {
      toast.error('Failed to update assignment');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'normal': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-gray-100 text-gray-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredAssignments = assignments.filter(assignment => {
    const matchesStatus = filterStatus === 'all' || assignment.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      assignment.scout_task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.athlete_profile.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusStats = () => {
    const stats = assignments.reduce((acc, assignment) => {
      acc[assignment.status] = (acc[assignment.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return {
      total: assignments.length,
      new: stats.new || 0,
      in_progress: stats.in_progress || 0,
      completed: stats.completed || 0
    };
  };

  const stats = getStatusStats();

  if (tasksLoading || assignmentsLoading) {
    return (
      <div className="px-4 md:px-8 lg:px-16 space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded mb-6"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:px-8 lg:px-16 space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-primary-700">Task Management</h1>
          <p className="text-sm md:text-base text-gray-600">
            Create task templates and assign them to athletes
          </p>
        </div>
        
        <Button 
          onClick={() => setIsAssignModalOpen(true)}
          className="w-full sm:w-auto min-h-[44px]"
        >
          <Users className="h-4 w-4 mr-2" />
          Assign Tasks
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <ClipboardList className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Assignments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                <div className="h-3 w-3 bg-gray-500 rounded-full"></div>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold">{stats.new}</p>
                <p className="text-xs text-muted-foreground">New</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <div className="h-3 w-3 bg-yellow-500 rounded-full"></div>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold">{stats.in_progress}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                <div className="h-3 w-3 bg-green-500 rounded-full"></div>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold">{stats.completed}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="templates" className="space-y-4">
        <TabsList>
          <TabsTrigger value="templates">Task Templates</TabsTrigger>
          <TabsTrigger value="assignments">Task Assignments</TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          <ReusableTaskCreator />
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Search by task or athlete name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="md:w-48">
                  <Label htmlFor="status">Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assignments List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Task Assignments ({filteredAssignments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredAssignments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {assignments.length === 0 ? (
                    <>
                      <ClipboardList className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium mb-2">No task assignments yet</p>
                      <p>Start by assigning tasks to your athletes</p>
                    </>
                  ) : (
                    <p>No assignments match your current filters</p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredAssignments.map((assignment) => (
                    <div key={assignment.id} className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-medium">{assignment.scout_task.title}</h4>
                            <Badge variant={getPriorityColor(assignment.scout_task.priority)}>
                              {assignment.scout_task.priority}
                            </Badge>
                            <Badge className={`text-xs ${getStatusColor(assignment.status)}`}>
                              {assignment.status === 'in_progress' ? 'In Progress' : assignment.status}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="font-medium">
                              {assignment.athlete_profile.name || assignment.athlete_profile.username}
                            </span>
                            {assignment.due_date && (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                <span>Due {new Date(assignment.due_date).toLocaleDateString()}</span>
                              </div>
                            )}
                            <span>Assigned {new Date(assignment.assigned_at).toLocaleDateString()}</span>
                          </div>
                          
                          {assignment.notes && (
                            <p className="text-sm text-muted-foreground mt-2">{assignment.notes}</p>
                          )}
                          
                          {assignment.scout_task.description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {assignment.scout_task.description}
                            </p>
                          )}

                          {assignment.updates && (
                            <div className="mt-2 p-2 bg-blue-50 rounded border-l-2 border-blue-300">
                              <p className="text-sm text-blue-800 font-medium">Latest Update:</p>
                              <p className="text-sm text-blue-700">{assignment.updates}</p>
                            </div>
                          )}
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditAssignment(assignment)}
                          className="ml-2"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AssignTaskModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onAssigned={handleTaskAssigned}
      />

      <EditAssignedTaskModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        assignment={editingAssignment}
        onSave={handleSaveAssignment}
      />
    </div>
  );
};

export default TasksPage;
